import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import CustomPopover, { CustomPopoverProps } from '@components/CustomPopover'
import InputWithTitleSection from '@components/InputWithTitleSection'

import { PopoverInner } from '../ShortlistProfileButton/index.styles'

interface EditCustomColumnPopoverProps {
  anchor: CustomPopoverProps['anchor']
  setAnchor: (
    event: Nullable<React.MouseEvent<HTMLElement>>,
    data?: { profileId: number; fieldId: number; customFieldValue: string },
  ) => void
  profileCustomColumnData: Nullable<{ customFieldValue?: string; profileId?: number; fieldId?: number }>
  deleteCustomColumnHandler: (cb?: () => void) => void
  setConfirmExitPopupCloseAction: (cb: { close: () => void }) => void
  updateCustomColumn: (data: { id: number; data: { customFieldValues: { [key: number]: string } } }) => void
}

const EditCustomColumnPopover = ({
  anchor,
  setAnchor,
  profileCustomColumnData,
  deleteCustomColumnHandler,
  setConfirmExitPopupCloseAction,
  updateCustomColumn,
}: EditCustomColumnPopoverProps) => {
  const { t } = useTranslation()
  const [localEditCustomColumnValue, setLocalEditCustomColumnValue] = useState<string>(
    profileCustomColumnData?.customFieldValue || '',
  )

  const handleClose = () => {
    const edited = profileCustomColumnData?.customFieldValue !== localEditCustomColumnValue.trim()

    if (edited) {
      setConfirmExitPopupCloseAction({ close: () => setAnchor(null) })
      return false
    }

    setAnchor(null)

    return true
  }

  const handleSubmit = (customFieldValue: string, cb: () => void) => {
    if (
      profileCustomColumnData?.profileId &&
      profileCustomColumnData?.fieldId &&
      profileCustomColumnData?.customFieldValue !== customFieldValue
    ) {
      updateCustomColumn({
        id: profileCustomColumnData?.profileId,
        data: { customFieldValues: { [profileCustomColumnData?.fieldId]: customFieldValue.trim() } },
      })
    } else {
      setAnchor(null)
    }

    // slight delay to let ui react before clearing the field
    setTimeout(() => {
      cb()

      setAnchor(null)
    }, 400)
  }
  return (
    anchor && (
      <CustomPopover id="custom_column_shortlist_profile_popover" anchor={anchor} setAnchor={handleClose}>
        <PopoverInner width={290}>
          <InputWithTitleSection
            multiline
            allowEmpty
            maxLength={100}
            onChange={val => setLocalEditCustomColumnValue(val.trim())}
            initialValue={profileCustomColumnData?.customFieldValue}
            title={t(profileCustomColumnData?.customFieldValue ? translationKeys.EDIT_TEXT : translationKeys.ADD_TEXT)}
            label={t(translationKeys.ADD_TEXT_HERE)}
            onCancel={handleClose}
            middleButtonText={profileCustomColumnData?.customFieldValue ? (t(translationKeys.DELETE_TEXT) as string) : ''}
            middleButtonOnClick={deleteCustomColumnHandler}
            submitButtonText={t(translationKeys.SAVE) as string}
            onSubmit={handleSubmit}
          />
        </PopoverInner>
      </CustomPopover>
    )
  )
}

export default memo(EditCustomColumnPopover)
